import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Button } from "src/components/ui/button";
import { useCreateClientSurveyAccessMutation, useDeleteClientSurveyAccessMutation, useGetClientSurveyAccessQuery, useGetOrganizationClientsQuery } from "src/pages/client-management/endpoints/client.endpoints";
import { ClientSurveyAccess } from "src/pages/client-management/types/client-management.types";
import { Client } from "src/pages/client-management/types/client.types";
import { pickColor } from "src/pages/client-management/utils";
import { showNotification } from "src/redux/notification/notification.slice";
import { Survey } from "src/redux/survey/survey.types";

interface SurveyAssignClientProps {
  activeSurvey: Survey | null;
  onCancel: () => void;
  onSave: () => void;
}

const SurveyAssignClient: React.FC<SurveyAssignClientProps> = ({ activeSurvey, onCancel, onSave }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredClients, setFilteredClients] = useState<Client[]>([]);
  const [selectedClients, setSelectedClients] = useState<Client[]>([]);
  const { data: clients } = useGetOrganizationClientsQuery();

  useEffect(() => {
    if (clients) {
      setFilteredClients(clients);
    }
  }, [clients]);

  useEffect(() => {
    if (searchQuery) {
      setFilteredClients(clients?.filter((client) => client.title.toLowerCase().includes(searchQuery.toLowerCase())) ?? []);
    } else {
      setFilteredClients(clients ?? []);
    }
  }, [searchQuery, clients]);

  const { data: clientSurveyAccess, refetch: refetchClientSurveyAccess } = useGetClientSurveyAccessQuery({ ext_survey_id: activeSurvey?.extSurveyId ?? '' });
  const [deleteClientSurveyAccess] = useDeleteClientSurveyAccessMutation();
  const [createClientSurveyAccess] = useCreateClientSurveyAccessMutation();
  const dispatch = useDispatch();

  useEffect(() => {
    setSelectedClients(clientSurveyAccess?.map((access) => clients?.find((client) => client.ext_client_id === access.client_id))?.filter((client) => client !== undefined) as Client[] ?? []);
  }, [clientSurveyAccess, clients]);


  const handleSelectClient = (client: Client) => {
    if (isSelectedClient(client)) {
      setSelectedClients(selectedClients.filter((selectedClient) => selectedClient.ext_client_id !== client.ext_client_id));
    } else {
      setSelectedClients([...selectedClients, client]);
    }
  };

  const handleCancel = () => {
    setSelectedClients([]);
    setSearchQuery('');
    onCancel();
  };

  const handleSave = async() => {
    const toRemove: ClientSurveyAccess[] = [];

    clientSurveyAccess?.forEach((access) => {
      if (!selectedClients.some((client) => client.ext_client_id === access.client_id)) {
        toRemove.push(access);
      }
    });

    if (toRemove.length > 0) {
      for (const access of toRemove) {
        await deleteClientSurveyAccess({ client_id: access.client_id, ext_survey_id: activeSurvey?.extSurveyId ?? '' });
      }

      dispatch(showNotification({ summary: 'Client(s) removed from survey', theme: 'success' }));
    }

    if (selectedClients.length > 0) {
      for (const client of selectedClients.filter((client) => !clientSurveyAccess?.some((access) => access.client_id === client.ext_client_id))) {
        const response = await createClientSurveyAccess({
          client_id: client.ext_client_id,
          ext_survey_id: activeSurvey?.extSurveyId ?? '',
        });
        if ('error' in response) {
          dispatch(showNotification({ summary: 'Error sharing survey with client', theme: 'warning' }));
          return;
        }
      }

      dispatch(showNotification({ summary: 'Survey(s) shared with client', theme: 'success' }));
      void refetchClientSurveyAccess();
      setSearchQuery('');
      setSelectedClients([]);
      onSave();
    }
  };

  const isSelectedClient = (client: Client) => {
    return selectedClients.some((selectedClient) => selectedClient.ext_client_id === client.ext_client_id);
  };

  return (
    <div className="flex flex-col">
      <div className="flex justify-between items-center">
        <h6 className="text-sm font-light">
          Assign Client
        </h6>
      </div>
      <div>
        <h2 className="text-xl font-semi-bold">
          {activeSurvey?.title}
        </h2>
      </div>
      <div className="mt-2">
        <div className="overflow-y-auto max-h-[75vh] p-2">
          <input
            type="text"
            placeholder="Search Clients"
            className="w-full p-2 border-b border-gray-400 focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-transparent"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {filteredClients.length > 0 && (
            filteredClients.map((client) => (
              <div
                key={client.ext_client_id}
                className={`flex items-center justify-between cursor-pointer hover:bg-gray-201 border-b border-gray-200 p-2 ${isSelectedClient(client) ? 'text-white bg-blue-500' : ''}`}
                onClick={() => handleSelectClient(client)}
              >
                <div className="flex items-center gap-2">
                  <div className={`size-6 rounded-full`}>
                    {client.brand ? (
                      <img
                        src={client.brand}
                        alt={client.title}
                        className="size-full object-contain bg-white rounded-full"
                      />
                    ) : <div className={`size-full object-fit ${pickColor(client.title, ['bg-green-501', 'bg-blue-500', 'bg-red-501', 'bg-yellow-500', 'bg-purple-500', 'bg-orange-500', 'bg-gray-500'])} rounded-full`} />}
                  </div>
                  <div className="text-sm font-medium">
                    {client.title}
                  </div>
                </div>
                <div className="text-sm text-gray-500">
                  {client.users.length}
                  {' '}
                  Users
                </div>
              </div>
            ))
          )}
        </div>
        <div className="mt-4 bg-gray-200 rounded-md p-2">
          <div>
            Selected Clients
          </div>
          <div className="flex flex-row gap-2 flex-wrap">
            {selectedClients.map((client) => (
              <div
                key={client.ext_client_id}
                className={`flex items-center text-xs text-black justify-between p-1 px-2 rounded-full bg-gray-201`}
              >
                {client.title}
              </div>
            ))}
          </div>
          <div className="absolute bottom-5 right-5 flex flex-row gap-2 justify-end">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleCancel()}
            >
              Cancel
            </Button>
            <Button
              disabled={selectedClients.length === 0}
              variant="primary"
              size="sm"
              onClick={() => handleSave()}
            >
              Save
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SurveyAssignClient;